import { createSelector } from '@reduxjs/toolkit';
import { toBig, validatePrecision } from '@talos/kyoko';
import { streamingDataSlice } from '../../../providers/AppStateProvider/streamingDataSlice';
import type { AppState } from '../../../providers/AppStateProvider/types';
import { isSpreadValid } from './utils';

/** Returns selectors scoped to the provided instanceID */
export function getRFQPricingSelectors(instanceID: string) {
  const selectState = (state: AppState) => state.manualRFQPricing[instanceID];
  const selectIsDestroyed = (state: AppState) => state.manualRFQPricing[instanceID] == null;

  /** Based on the current pricing method, returns the bid-asks the user is currently using to price the Quote */
  const selectUsedBidAsks = createSelector(
    (state: AppState) => state.manualRFQPricing[instanceID].form.compositeBidAsk,
    (state: AppState) => state.manualRFQPricing[instanceID].form.legBidAsks,
    (state: AppState) => state.manualRFQPricing[instanceID].form.pricingMethod,
    (composite, legs, pricingMethod) => {
      return pricingMethod === 'composite' ? [composite] : legs;
    }
  );

  /** Looks at the currently used bid-ask spread and checks if any bid-ask spread is crossed */
  const selectAnySpreadError = createSelector(selectUsedBidAsks, (bidAsks): string | undefined => {
    const someCrossedSpread = bidAsks.some(
      bidAsk => !isSpreadValid({ bid: bidAsk.bid.bigValue, ask: bidAsk.ask.bigValue })
    );
    return someCrossedSpread ? 'Crossed spread' : undefined;
  });

  /** Specifically looks at the composite bid-ask spread and checks if it is crossed */
  const selectCompositeSpreadError = createSelector(
    (state: AppState) => state.manualRFQPricing[instanceID].form.compositeBidAsk,
    ({ bid, ask }): string | undefined => {
      return isSpreadValid({ bid: bid.bigValue, ask: ask.bigValue }) ? undefined : 'Crossed spread';
    }
  );

  /** Looks at the individual fields that are currently being used and checks that they all have valid values */
  const selectAnyFieldError = createSelector(selectUsedBidAsks, (bidAsks): string | undefined => {
    const someInvalidField = bidAsks.some(bidAsk => bidAsk.bid.hasInvalidValue || bidAsk.ask.hasInvalidValue);
    return someInvalidField ? 'Invalid pricing field(s)' : undefined;
  });

  const selectCompositeQtyError = createSelector(
    (state: AppState) => state.manualRFQPricing[instanceID].form.compositeBidAsk,
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote?.OrderQty,
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote?.MinSizeIncrement,
    ({ bidQty, askQty }, orderQty, minSizeIncrement): string | undefined => {
      return (
        getQtyFieldError({ field: bidQty, orderQty, minSizeIncrement, side: 'bid' }) ??
        getQtyFieldError({ field: askQty, orderQty, minSizeIncrement, side: 'ask' })
      );
    }
  );

  const selectBidQtyError = createSelector(
    (state: AppState) => state.manualRFQPricing[instanceID].form.compositeBidAsk.bidQty,
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote?.OrderQty,
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote?.MinSizeIncrement,
    (bidQty, orderQty, minSizeIncrement): string | undefined => {
      return getQtyFieldError({ field: bidQty, orderQty, minSizeIncrement, side: 'bid' });
    }
  );

  const selectAskQtyError = createSelector(
    (state: AppState) => state.manualRFQPricing[instanceID].form.compositeBidAsk.askQty,
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote?.OrderQty,
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote?.MinSizeIncrement,
    (askQty, orderQty, minSizeIncrement): string | undefined => {
      return getQtyFieldError({ field: askQty, orderQty, minSizeIncrement, side: 'ask' });
    }
  );

  /** Combines the spread crossed error checking and the individual field checking to see if there is any error _at all_ in the entire form */
  const selectBidAsksError = createSelector(
    selectAnySpreadError, // all fields are valid in combination (tuples)
    selectAnyFieldError, // all fields are valid on their own
    selectCompositeQtyError,
    (spreadResult, individualResult, compositeQtyError): string | undefined => {
      return spreadResult ?? individualResult ?? compositeQtyError;
    }
  );

  const selectCanRejectQuote = createSelector(
    (state: AppState) => state.manualRFQPricing[instanceID].isLoading,
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote,
    (isLoading, currentCustomerQuote): { canRejectQuote: boolean; text?: string } => {
      if (currentCustomerQuote == null) {
        return { canRejectQuote: false, text: 'Unable to find quote' };
      }

      if (isLoading) {
        // No text returned here - the button will be spinning which is enough
        return { canRejectQuote: false, text: undefined };
      }
      if (!currentCustomerQuote.isOpen) {
        return { canRejectQuote: false, text: `Quote is not open (${currentCustomerQuote.QuoteStatus})` };
      }
      return { canRejectQuote: true };
    }
  );

  const selectCanSendQuote = createSelector(
    (state: AppState) => state.manualRFQPricing[instanceID].isLoading,
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuote,
    selectBidAsksError,
    (isLoading, currentCustomerQuote, bidAsksError): { canSendQuote: boolean; text?: string } => {
      if (currentCustomerQuote == null) {
        return { canSendQuote: false, text: 'Unable to find quote' };
      }

      if (isLoading) {
        // No text returned here - the button will be spinning which is enough
        return { canSendQuote: false, text: undefined };
      }
      if (!currentCustomerQuote.isOpen) {
        return { canSendQuote: false, text: `Quote is not open (${currentCustomerQuote.QuoteStatus})` };
      }
      if (bidAsksError) {
        return { canSendQuote: false, text: bidAsksError };
      }

      return { canSendQuote: true };
    }
  );

  const selectCustomerQuoteRequestParams = createSelector(
    (state: AppState) => state.manualRFQPricing[instanceID].currentCustomerQuoteRFQID,
    rfqID => {
      return rfqID ? { RFQID: rfqID } : undefined;
    }
  );

  const selectCustomerQuote = createSelector(
    selectCustomerQuoteRequestParams,
    (state: AppState) => state,
    (query, state) => {
      if (!query) {
        return undefined;
      }
      return streamingDataSlice.endpoints.getCustomerQuotes.select(query)(state).data?.get(query.RFQID);
    }
  );

  return {
    selectState,
    selectIsDestroyed,
    selectUsedBidAsks,
    selectAnyFieldError,
    selectCompositeSpreadError,
    selectCanRejectQuote,
    selectCanSendQuote,
    selectCustomerQuoteRequestParams,
    selectCustomerQuote,
    selectBidAsksError,
    selectBidQtyError,
    selectAskQtyError,
  };
}

function getQtyFieldError({
  field,
  orderQty,
  minSizeIncrement,
  side,
}: {
  field: AppState['manualRFQPricing'][string]['form']['compositeBidAsk']['bidQty'];
  orderQty?: string;
  minSizeIncrement?: string;
  side: 'bid' | 'ask';
}): string | undefined {
  const label = side === 'bid' ? 'Bid Quantity' : 'Offer Quantity';

  if (field.isDisabled) {
    return undefined;
  }

  if (field.isRequired && !field.hasValue) {
    return `${label} is required`;
  }

  if (!field.hasValue) {
    return undefined;
  }

  const qtyValue = field.value;
  const qty = qtyValue == null ? undefined : toBig(qtyValue);
  if (qty?.lte(0)) {
    return `${label} must be greater than 0`;
  }

  if (orderQty && qty?.gt(orderQty)) {
    return `${label} cannot exceed order quantity: ${orderQty}`;
  }

  if (minSizeIncrement && qtyValue != null && !validatePrecision(minSizeIncrement, qtyValue)) {
    return `${label} min increment is ${minSizeIncrement}`;
  }

  return field.hasInvalidValue ? `Invalid ${label.toLowerCase()}` : undefined;
}
